import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { DuplicateCandidate } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import {
  useCompareCustomers,
  useDismissDuplicate,
  useDuplicates,
  useScanDuplicates,
} from '../features/customers.js';
import { MergeModal } from '../components/MergeModal.js';
import { EmptyState, ErrorState, InlineError, LoadingState, Modal, PageHeader, Pill } from '../components/ui.js';
import { IconAlertTriangle, IconSearch } from '../components/icons.js';

const scoreKind = (s: number) => (s >= 85 ? 'hold' : s >= 70 ? 'warn' : 'info');
/** a dialable tel: link from a typed number */
const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`;

// ── Side-by-side compare modal ──────────────────────────────────────────────────
const CMP_FIELDS: Array<[string, (c: Record<string, unknown>) => string]> = [
  ['Type', (c) => String(c.customerType ?? '')],
  ['Company', (c) => String(c.companyName ?? '')],
  ['Name', (c) => `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()],
  ['Email', (c) => String(c.email ?? '')],
  ['Telephone', (c) => String(c.telephone ?? '')],
  ['Mobile', (c) => String(c.mobile ?? '')],
  ['VAT number', (c) => String(c.vatNumber ?? '')],
  ['Tax code', (c) => String(c.taxCode ?? '')],
  ['Profession', (c) => String(c.profession ?? '')],
  ['Dealer', (c) => String(c.dealer ?? '')],
  ['Address', (c) => String(c.registeredAddress ?? '')],
  ['Country', (c) => String(c.country ?? '')],
];

function CompareModal({ aId, bId, onClose }: { aId: number; bId: number; onClose: () => void }) {
  const { data, isLoading, error } = useCompareCustomers(aId, bId);
  // the record each value line belongs to, as the column heads read (★ = suggested primary)
  const srcA = `#${aId}${data?.suggestedPrimaryId === aId ? ' ★' : ''}`;
  const srcB = `#${bId}${data?.suggestedPrimaryId === bId ? ' ★' : ''}`;
  return (
    <Modal title="Compare customers" width={760} onClose={onClose} footer={<button className="btn btn-ghost" onClick={onClose}>Close</button>}>
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} />}
      {data && data.a && data.b && (
        <>
          <div className="cfg-sum-row">
            <span>Confidence</span>
            <span><Pill kind={scoreKind(data.result.score)}>{Math.round(data.result.score)}%</Pill>{data.result.strongConflict && <>{' '}<Pill kind="hold">strong conflict</Pill></>}</span>
          </div>
          <div className="cfg-sum-row"><span>Suggested primary</span><span>#{data.suggestedPrimaryId} ({data.suggestedPrimaryId === aId ? `${data.aOrders} orders` : `${data.bOrders} orders`})</span></div>
          {/* phones stack each field: its name, then one line per record (data-src names the record) */}
          <table className="tbl merge-resolve" style={{ marginTop: 10 }}>
            <thead><tr><th>Field</th><th>{srcA}</th><th>{srcB}</th></tr></thead>
            <tbody>
              {CMP_FIELDS.map(([label, get]) => {
                const av = get(data.a as unknown as Record<string, unknown>);
                const bv = get(data.b as unknown as Record<string, unknown>);
                const both = av && bv;
                const same = both && av.toLowerCase() === bv.toLowerCase();
                const differ = both && !same;
                return (
                  <tr key={label}>
                    {/* the "differs" pill is the text equivalent of the red/green cell tint */}
                    <td className="muted">{label}{differ && <>{' '}<Pill kind="warn">differs</Pill></>}</td>
                    <td data-src={srcA} style={{ background: same ? 'var(--ok-bg)' : differ ? 'var(--err-bg)' : undefined }}>{av || '-'}</td>
                    <td data-src={srcB} style={{ background: same ? 'var(--ok-bg)' : differ ? 'var(--err-bg)' : undefined }}>{bv || '-'}</td>
                  </tr>
                );
              })}
              <tr><td className="muted">Orders</td><td data-src={srcA}>{data.aOrders}</td><td data-src={srcB}>{data.bOrders}</td></tr>
            </tbody>
          </table>
        </>
      )}
    </Modal>
  );
}

// ── candidate card ──────────────────────────────────────────────────────────────
function Party({ p }: { p: DuplicateCandidate['a'] }) {
  const info = [p.companyName, p.vatNumber && `VAT ${p.vatNumber}`, p.country].filter(Boolean).join(' · ');
  return (
    <div className="dup-party">
      <div className="dup-party-name">
        <span>{p.displayName}</span> <Pill kind="info">{p.customerType === 'company' ? 'Co' : 'Ind'}</Pill>
      </div>
      <div className="muted dup-party-sub">#{p.id} · {p.orderCount} order{p.orderCount === 1 ? '' : 's'}</div>
      {(info || (!p.email && !p.telephone)) && <div className="dup-party-info">{info || '-'}</div>}
      {p.email && <a className="dup-party-link" href={`mailto:${p.email}`}>{p.email}</a>}
      {p.telephone && <a className="dup-party-link" href={telHref(p.telephone)}>{p.telephone}</a>}
    </div>
  );
}

function Card({ c, onCompare, onMerge }: { c: DuplicateCandidate; onCompare: (a: number, b: number) => void; onMerge: (a: number, b: number) => void }) {
  const { can } = useAuth();
  const dismiss = useDismissDuplicate();
  return (
    <div className="card dup-card">
      <div className="dup-card-hd">
        <Pill kind={scoreKind(c.score)}>{Math.round(c.score)}% match</Pill>
        {c.strongConflict && <Pill kind="hold"><IconAlertTriangle size={12} /> strong conflict</Pill>}
        <span className="muted">Suggested primary: #{c.suggestedPrimaryId}</span>
      </div>

      <div className="dup-parties">
        <Party p={c.a} />
        <div className="dup-vs" aria-hidden="true"><span className="dup-vs-ic">↔</span><span className="dup-vs-txt">vs</span></div>
        <Party p={c.b} />
      </div>

      <div className="dup-tags">
        {c.matching.map((m, i) => (
          <span key={`m${i}`} className="pill ok">{m.kind === 'exact' ? `Same ${m.field.toLowerCase()}` : `~ ${m.field.toLowerCase()} ${m.detail ?? ''}`}</span>
        ))}
        {/* each difference shows both values under its pill (a tooltip has no touch path) */}
        {c.conflicting.map((cf, i) => (
          <span key={`c${i}`} className="dup-conf">
            <span className="pill hold">{cf.field} differs</span>
            {/* both values in full; each side ellipsizes only when the pair is wider than the card (CSS) */}
            <span className="dup-conf-vals" title={`${cf.a || '-'} ≠ ${cf.b || '-'}`}>
              <span className="dup-conf-v">{cf.a || '-'}</span>
              <span className="dup-conf-ne">≠</span>
              <span className="dup-conf-v">{cf.b || '-'}</span>
            </span>
          </span>
        ))}
      </div>

      <div className="dup-acts">
        <button className="btn btn-sm" onClick={() => onCompare(c.a.id, c.b.id)}>Compare</button>
        {can('customer_database.merge') && (
          <button className="btn btn-sm btn-ghost" disabled={dismiss.isPending} onClick={() => dismiss.mutate(c.id)}>Not a duplicate</button>
        )}
        {can('customer_database.merge') && (
          <button className="btn btn-sm btn-primary dup-merge" onClick={() => onMerge(c.a.id, c.b.id)}>Merge…</button>
        )}
      </div>
      {dismiss.isError && <InlineError error={dismiss.error} onDismiss={() => dismiss.reset()} />}
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
export function CustomerDuplicates() {
  const { can } = useAuth();
  const { data, isLoading, isFetching, error, refetch } = useDuplicates();
  const scan = useScanDuplicates();
  const [cmp, setCmp] = useState<{ a: number; b: number } | null>(null);
  const [mrg, setMrg] = useState<{ a: number; b: number } | null>(null);

  const candidates = data?.candidates ?? [];

  return (
    <>
      <PageHeader title="Possible Duplicate Customers">
        <Link to="/sales/customers" className="btn btn-sm btn-ghost">← Customer Database</Link>
        <span className="muted page-count">{candidates.length} open</span>
        {can('customer_database.merge') && (
          <button className="btn btn-sm btn-primary" disabled={scan.isPending} onClick={() => scan.mutate()}>
            {scan.isPending ? 'Scanning…' : <><IconSearch size={14} /> Scan for duplicates</>}
          </button>
        )}
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Refreshing…' : 'Refresh'}</button>
      </PageHeader>

      {scan.data && (
        <div className="muted" style={{ marginBottom: 10 }}>
          Scanned {scan.data.customers} customers · {scan.data.pairs} candidate pairs · {scan.data.candidates} flagged.
        </div>
      )}

      {isLoading && <LoadingState label="Loading duplicates…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {scan.isError && <InlineError error={scan.error} onDismiss={() => scan.reset()} />}
      {data && candidates.length === 0 && (
        <EmptyState title="No possible duplicates" hint={can('customer_database.merge') ? 'Run a scan to check for duplicates across all customers.' : 'Nothing flagged for review.'} />
      )}

      {candidates.map((c) => (
        <Card key={c.id} c={c} onCompare={(a, b) => setCmp({ a, b })} onMerge={(a, b) => setMrg({ a, b })} />
      ))}

      {cmp && <CompareModal aId={cmp.a} bId={cmp.b} onClose={() => setCmp(null)} />}
      {mrg && <MergeModal aId={mrg.a} bId={mrg.b} onClose={() => setMrg(null)} />}
    </>
  );
}
